import { useEffect, useRef, useCallback } from "react";
import { getItinerary } from "../libraries/api";

const DOCUMENT_URL_RENEW_INTERVAL_MS = 19 * 60 * 1000; // 19 minutes in milliseconds
const SAFETY_MARGIN_SEC = 60; // 1 minute in sec

const getExpiryFromUrl = (url) => {
  const match = url?.match(/[?&]Expires=(\d+)/);
  return match ? parseInt(match[1], 10) : null;
};

const getDocuments = async (itin) => {
  try {
    const travellerId = itin?.clients?.find(client => client.isCurrent)?.id;
    const { data } = await getItinerary(itin.operatorCode, itin.passcode, 'refresh', 'false', travellerId);
    return data?.documents || [];
  } catch (error) {
    console.error("Failed to refresh documents:", error);
    return [];
  }
};

export const useRefreshDocumentUrls = (itin, setItin, previewMode) => {
  const timeoutRef = useRef(null);
  const doRenewRef = useRef(null);
  const scheduleNextRef = useRef(null);

  const doRenew = useCallback(async () => {
    const docs = itin?.documents || [];
    if (!docs || !docs.length) return;

    const updatedDocs = await getDocuments(itin);
    const updatedMap = Object.fromEntries(updatedDocs.map(doc => [doc.id, doc]));
    const hasChange = docs.some(doc => {
      const updated = updatedMap[doc.id];
      return !!(
        updated?.file?.httpsUrl &&
        doc?.file?.httpsUrl &&
        updated.file.httpsUrl !== doc.file.httpsUrl
      );
    });

    if (!hasChange) {
      scheduleNextRef.current(DOCUMENT_URL_RENEW_INTERVAL_MS);
      return;
    }

    const newDocs = docs.map(doc => {
      const updated = updatedMap[doc.id];
      if (doc?.file?.httpsUrl && updated?.file?.httpsUrl) {
        return {
          ...doc,
          file: {
            ...doc.file,
            httpsUrl: updated.file.httpsUrl
          }
        };
      }
      return doc;
    });

    setItin(prev => ({ ...prev, documents: newDocs }));

    // Schedule next renew after fixed interval
    scheduleNextRef.current(DOCUMENT_URL_RENEW_INTERVAL_MS);
  }, [itin, setItin]);

  const scheduleNext = useCallback((ms) => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      doRenewRef.current();
    }, ms);
  }, []);

  // Keep refs updated with latest functions
  // This ensures setTimeout callbacks always use up-to-date logic,
  // avoiding stale closures and allowing mutual references between doRenew and scheduleNext.
  doRenewRef.current = doRenew;
  scheduleNextRef.current = scheduleNext;

  const startDocumentRefreshCycle = useCallback(() => {
    if (!itin?.documents?.length || previewMode) {
      // If documents become empty, stop any pending renewals
      clearTimeout(timeoutRef.current);
      return;
    }

    const firstUrl = itin.documents.find(doc => doc?.file?.httpsUrl)?.file?.httpsUrl;
    const expiresAt = getExpiryFromUrl(firstUrl);

    const nowInSec = Math.floor(Date.now() / 1000);
    let timeUntilRenewMs = DOCUMENT_URL_RENEW_INTERVAL_MS;

    if (expiresAt) {
      const timeLeft = expiresAt - nowInSec - SAFETY_MARGIN_SEC;
      timeUntilRenewMs = Math.max(1000, timeLeft * 1000); // at least 1 sec
    }
    scheduleNext(timeUntilRenewMs);
  }, [itin?.documents, previewMode, scheduleNext]);

  useEffect(() => {
    startDocumentRefreshCycle();

    return () => clearTimeout(timeoutRef.current);
  }, [itin?.id, previewMode, startDocumentRefreshCycle]);
};